<template>
  <v-row no-gutters class="pt-5">
    <v-col class="pr-2">
      <card-title-row translation-path="sidebar.quickCheck.title" :has-buttons="false" />
      <sidebar-row>
        <v-col class="text-left px-0 pb-0">
          <v-tooltip bottom :disabled="junctionsAdded && !isJunctionSelected" max-width="300">
            <template #activator="{ on }">
              <span v-on="on">
                <base-button-subheader subheader-translation-path="sidebar.quickCheck.selectJunctionSubtitle">
                  <base-button
                    @click="toggleSelecting"
                    class="mr-0"
                    translation-path="sidebar.quickCheck.selectJunction"
                    :button-style="getButtonStyle(isSelectionActive)"
                    :disabled="!junctionsAdded || (isJunctionSelected && !isSelectionActive)"
                  />
                </base-button-subheader>
              </span>
            </template>
            {{ buttonTooltip }}
          </v-tooltip>
        </v-col>
      </sidebar-row>
      <template v-if="isJunctionSelected">
        <sidebar-row class="pr-5">
          <v-list class="py-0">
            <template v-for="feature in computedSelectedJunctions">
              <v-list-item :key="feature" class="hover-list-element">
                <v-list-item-content class="pa-0">
                  <v-list-item-title>
                    <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                      ID: {{ feature }}
                    </v-subheader>
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-icon
                    @click="deleteFeature(feature)"
                    color="rgba(0,0,0,.7)"
                    size="19px"
                    :disabled="isStickyButtonsLoading"
                  >
                    mdi-close
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </sidebar-row>
      </template>
      <v-form ref="quickCheckForm" v-model="isValid">
        <sidebar-row class="pt-6">
          <v-row no-gutters class="justify-space-between">
            <v-col cols="6" class="text-body-2 center-content text-left pt-3">
              {{ $i18n.t('sidebar.quickCheck.waterDissolutionPlannedValue') }}:
            </v-col>
            <v-col cols="5">
              <data-input v-bind="waterDissolutionValueConfig" v-model="waterDissolutionValue" />
            </v-col>
          </v-row>
        </sidebar-row>
      </v-form>
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';

import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'QuickCheck',
  components: { CardTitleRow, DataInput, SidebarRow },
  computed: {
    activeTool: get('tools/activeTool'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },

    selectedJunctions: get('map/selectedFeatures@:waterJunctionsLayerId'),
    computedSelectedJunctions() {
      return this.selectedJunctions || [];
    },
    selectedJunction() {
      return this.computedSelectedJunctions[0] || undefined;
    },

    layers: get('layers/layers'),
    buttonTooltip() {
      return this.junctionsAdded
        ? this.$i18n.t('sidebar.quickCheck.onlyOneJunction')
        : this.$i18n.t(`sidebar.quickCheck.junctionLayerNotAdded`);
    },
    junctionsAdded() {
      return this.waterJunctionProjectLayer ? true : false;
    },
    isJunctionSelected() {
      return this.selectedJunction ? true : false;
    },
    waterJunctionsLayer() {
      return this.$_objectToArray(this.layers).find(layer => layer.role === 'water_junctions');
    },
    waterJunctionProjectLayer() {
      return this.projectLayers.find(layer => layer.role === 'water_junctions') ? true : false;
    },
    waterJunctionsLayerId() {
      return this.waterJunctionsLayer?.id;
    },

    waterDissolutionValueConfig() {
      return {
        label: this.$i18n.t('dialog.body.value'),
        dataType: { name: 'float' },
        rules: [v => !!v || v === 0 || this.$i18n.t('rules.required')],
      };
    },

    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    stickyButtons: sync('sidebar/stickyButtons'),
  },
  data: () => ({
    isSelectionActive: false,
    isValid: false,
    waterDissolutionValue: null,

    modelAdditionalParameters: localStorage.getItem('additionalGraphParameters')
      ? JSON.parse(localStorage.getItem('additionalGraphParameters'))
      : [],
    modelNetKind: localStorage.getItem('netKindModel') ? JSON.parse(localStorage.getItem('netKindModel')) : [],
    modelStatus: localStorage.getItem('statusModel') ? JSON.parse(localStorage.getItem('statusModel')) : [],
  }),
  methods: {
    runModel: call('epanet/runModel'),
    clearSelection(layerId) {
      this.turnOffSelecting();
      if (layerId || layerId === 0) {
        this.deleteFeature(null, layerId);
      } else {
        this.deleteFeature(null);
      }
    },
    deleteFeature(id, layerId = this.waterJunctionsLayerId) {
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    getButtonStyle(isButtonActive) {
      return isButtonActive
        ? {
            backgroundColor: '#E8F0FE !important',
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
    toggleSelecting() {
      this[`turn${this.isSelectionActive ? 'Off' : 'On'}Selecting`]();
    },
    turnOffSelecting() {
      if (this.activeTool !== 'selection') {
        return;
      }
      this.$root.$emit('selection-action');
      this.isSelectionActive = false;
    },
    turnOnSelecting() {
      if (this.junctionsAdded && this.waterJunctionsLayerId) {
        this.$root.$emit('setProjectLayerStyle', this.waterJunctionsLayerId, {
          selectedColors: { selectedColor: '#FF0000', haloColorSelected: '#FF0000' },
        });
      }
      this.$root.$emit('attachOnClickSelection', this.waterJunctionsLayerId);
      this.isSelectionActive = true;
    },

    getPayload() {
      const additionalParamsArray = [];
      this.modelAdditionalParameters.forEach(param => {
        if (param.checked) {
          additionalParamsArray.push({
            filter: param.filter,
            datasource: param.datasource,
          });
        }
      });
      const junctionsDemand = {
        junctions: [this.selectedJunction],
      };
      junctionsDemand['value'] = this.waterDissolutionValue;
      const payload = {
        status: this.modelStatus,
        net_kind: this.modelNetKind,
        features_filter: additionalParamsArray,
        junctions_demand: junctionsDemand,
      };
      return payload;
    },
    async runQuickCheck() {
      this.isStickyButtonsLoading = true;
      try {
        const payload = this.getPayload();
        const r = await this.runModel(payload);
        this.$router.push({
          name: 'modelingResults',
          params: {
            netType: 'water',
            scenarioId: r.scenario_id,
          },
        });
        localStorage.setItem(`scenarioId-water`, r.scenario_id);
      } finally {
        this.isStickyButtonsLoading = false;
      }
    },
  },
  watch: {
    activeTool(nV, oV) {
      if (nV !== oV && oV === 'selection') {
        this.isSelectionActive = false;
      }
    },
    isJunctionSelected(nV) {
      if (nV) {
        this.turnOffSelecting();
      }
    },
    junctionsAdded(nV) {
      if (!nV) {
        this.clearSelection();
      }
    },
  },
  mounted() {
    this.$root.$off('runQuickCheck');
    this.$root.$on('runQuickCheck', this.runQuickCheck);
    this.stickyButtons = [
      {
        action: 'runQuickCheck',
        translationPath: 'dialog.agreeCheck',
      },
    ];
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = true;
    this.$watch(
      vm => {
        return { formValid: vm.isValid, selectedFeature: vm.selectedJunction };
      },
      nV => {
        const { formValid, selectedFeature } = nV;
        this.isStickyButtonsValid = formValid && selectedFeature ? true : false;
      }
    );
  },
  beforeDestroy() {
    this.clearSelection();
    if (this.junctionsAdded && this.waterJunctionsLayerId) {
      this.$root.$emit('setProjectLayerStyle', this.waterJunctionsLayerId);
    }
    this.isStickyButtonsVisible = false;
    this.stickyButtons = [];
  },
};
</script>
<style lang="scss" scoped>
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
</style>
